import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  GraduationCap, BookOpen, Users2, ClipboardCheck, Plus, Pencil, Trash2,
  Calendar, MapPin, Clock, X, CheckCircle2, AlertTriangle, Play, Lock,
  FileSignature, ChevronRight, Building2, Globe,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Training & Coms — unified hub for the Training program catalog, hosted
// Sessions (F01) and per-employee Induction checklists (F03). The legacy
// per-member Training tab (F02) keeps its place inside member profiles.
// ---------------------------------------------------------------------------
const TABS = [
  { id: "programs",  label: "Programs",  icon: BookOpen },
  { id: "sessions",  label: "Sessions",  icon: Users2 },
  { id: "induction", label: "Induction", icon: ClipboardCheck },
];

export default function TrainingComs() {
  const [params, setParams] = useSearchParams();
  const { isAllSites, activeSite } = useSite();
  const tab = TABS.find((t) => t.id === params.get("tab"))?.id || "programs";

  const switchTab = (id) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!params.get("tab")) {
      const next = new URLSearchParams(params);
      next.set("tab", "programs");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="vb-fade-in" data-testid="training-coms-page">
      <header className="mb-4">
        <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
          <GraduationCap size={24} className="inline-block mr-2 -mt-1" />
          Training &amp; Coms
        </h1>
        <p className="text-sm opacity-70 m-0">
          Define training programs, host live sessions, evaluate attendees, and track new-hire induction (VB-P-072 F01 &amp; F03).
        </p>
      </header>

      {/* Scope banner — reflects the global site filter so users always know
          whether they're creating corporate-wide or site-specific records. */}
      <div
        className="vb-card flex items-center gap-3 mb-3 px-4 py-2.5"
        style={{
          background: isAllSites ? "rgba(46, 99, 12, 0.06)" : "rgba(123, 74, 45, 0.08)",
          borderLeft: `3px solid ${isAllSites ? "var(--vb-accent)" : "var(--vb-text-2)"}`,
        }}
        data-testid={`tc-scope-banner-${isAllSites ? "all" : "site"}`}
      >
        {isAllSites
          ? <Globe size={16} style={{ color: "var(--vb-accent)", flexShrink: 0 }} />
          : <Building2 size={16} style={{ color: "var(--vb-text-2)", flexShrink: 0 }} />}
        <div className="flex-1 text-xs">
          {isAllSites ? (
            <>
              <strong>Viewing all sites.</strong> New programs default to corporate-wide (apply to every site). Sessions default to Corporate-wide unless you pick a host site.
            </>
          ) : (
            <>
              <strong>Viewing site: {activeSite?.name}.</strong> New records you create here will be scoped to this site by default. Switch back to "All sites" from the header to create corporate-wide records.
            </>
          )}
        </div>
      </div>

      <div className="vb-tabs mb-4" role="tablist" data-testid="training-coms-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id} role="tab" aria-selected={active}
              className={`vb-tab ${active ? "active" : ""}`}
              onClick={() => switchTab(t.id)}
              data-testid={`training-coms-tab-${t.id}`}
            >
              <Icon size={14} className="inline-block mr-1.5 -mt-0.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "programs" && <ProgramsTab />}
      {tab === "sessions" && <SessionsTab onSwitchTab={switchTab} />}
      {tab === "induction" && <InductionTab />}
    </div>
  );
}


// ============================================================================
//  PROGRAMS — catalog / library of reusable training templates
// ============================================================================
const CATEGORIES = ["FSMS", "HACCP", "GMP", "Allergens", "Pest Control",
  "Emergency", "Onboarding", "SOP", "Other"];
const COMPETENCY = {
  1: { code: "CT", label: "Completed Training" },
  2: { code: "API", label: "Able to perform independently" },
  3: { code: "ATC", label: "Approved to coach others" },
};

function ProgramsTab() {
  const { user } = useAuth();
  const { activeSiteId, isAllSites, sites } = useSite();
  const { confirm } = useDialog();
  const canEdit = ["owner", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeSiteId) params.site_id = activeSiteId;
      const { data } = await api.get("/training/programs", { params });
      setItems(data.programs || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load programs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId]);

  const filtered = useMemo(() => {
    if (!filter) return items;
    return items.filter((p) => p.category === filter);
  }, [items, filter]);

  const del = async (p) => {
    if (!await confirm({
      title: `Delete program "${p.title}"?`,
      message: "Existing sessions that referenced this program will be unlinked (their snapshot is preserved).",
      danger: true, confirmLabel: "Delete program",
    })) return;
    try { await api.delete(`/training/programs/${p.id}`); load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  if (loading) return <div className="vb-card p-6 opacity-70" data-testid="programs-loading">Loading programs…</div>;

  return (
    <div data-testid="programs-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs opacity-70">Filter:</span>
          <select className="vb-input text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="programs-filter">
            <option value="">All categories ({items.length})</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {canEdit && (
          <button className="vb-btn text-sm" data-testid="add-program-btn"
            onClick={() => setModal({
              mode: "add",
              form: { ...blankProgram(), site_ids: isAllSites ? [] : (activeSiteId ? [activeSiteId] : []) },
            })}>
            <Plus size={14} /> New program
          </button>
        )}
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="programs-error">{err}</div>}

      {filtered.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="programs-empty">
          <BookOpen size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm m-0">No training programs yet.</p>
          {canEdit && <p className="text-xs mt-1">Create a program template once, then reuse it across many sessions.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="programs-grid">
          {filtered.map((p) => (
            <ProgramCard key={p.id} program={p}
              canEdit={canEdit}
              onEdit={() => setModal({ mode: "edit", id: p.id, form: programToForm(p) })}
              onDelete={() => del(p)} />
          ))}
        </div>
      )}

      {modal && (
        <ProgramModal
          mode={modal.mode} id={modal.id} initial={modal.form}
          sites={sites}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function ProgramCard({ program: p, canEdit, onEdit, onDelete }) {
  const c = COMPETENCY[p.target_competency_level] || COMPETENCY[1];
  const corporate = !p.site_ids || p.site_ids.length === 0;
  return (
    <div className="vb-card p-4 border" data-testid={`program-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-sm">{p.title}</strong>
            <span className="vb-pill text-[10px]" style={{ background: "rgba(123, 74, 45, 0.12)" }}>{p.category}</span>
            <span className="vb-pill text-[10px] font-mono" title={c.label}>{c.code}</span>
            {corporate ? (
              <span className="vb-pill ok text-[10px]" title="Applies to every site"><Globe size={10} className="inline -mt-0.5 mr-0.5" /> Corporate-wide</span>
            ) : (
              <span className="vb-pill text-[10px]" title={`Applies to ${p.site_ids.length} site${p.site_ids.length === 1 ? "" : "s"}`}>
                <Building2 size={10} className="inline -mt-0.5 mr-0.5" /> {p.site_ids.length} site{p.site_ids.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <p className="text-xs opacity-70 mt-1 mb-0">{p.description || <span className="opacity-50">No description.</span>}</p>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <button className="vb-btn ghost" onClick={onEdit} title="Edit" data-testid={`edit-program-${p.id}`}><Pencil size={13} /></button>
            <button className="vb-btn ghost" onClick={onDelete} title="Delete" data-testid={`delete-program-${p.id}`}><Trash2 size={13} /></button>
          </div>
        )}
      </div>
      <div className="text-xs opacity-70 flex flex-wrap items-center gap-3 mb-2">
        <span><Clock size={11} className="inline -mt-0.5 mr-1" />{p.default_duration_minutes} min</span>
        {p.default_retrain_months ? (
          <span>Retrain every {p.default_retrain_months} month{p.default_retrain_months === 1 ? "" : "s"}</span>
        ) : (
          <span className="opacity-50">No retrain cadence</span>
        )}
      </div>
      {p.topics?.length > 0 && (
        <div className="text-xs">
          <div className="opacity-60 mb-1">Topics ({p.topics.length}):</div>
          <ul className="m-0 pl-4 opacity-80 space-y-0.5">
            {p.topics.slice(0, 4).map((t, i) => <li key={i}>{t}</li>)}
            {p.topics.length > 4 && <li className="opacity-60">+{p.topics.length - 4} more</li>}
          </ul>
        </div>
      )}
      {p.materials_url && (
        <a href={p.materials_url} target="_blank" rel="noreferrer" className="vb-link text-xs mt-2 inline-block">
          Materials →
        </a>
      )}
    </div>
  );
}

const blankProgram = () => ({
  title: "", description: "", category: "FSMS",
  default_duration_minutes: 60, target_competency_level: 1,
  default_retrain_months: 12, materials_url: "", topics: [],
  site_ids: [],
});
const programToForm = (p) => ({
  title: p.title || "", description: p.description || "",
  category: p.category || "FSMS",
  default_duration_minutes: p.default_duration_minutes || 60,
  target_competency_level: p.target_competency_level || 1,
  default_retrain_months: p.default_retrain_months ?? 12,
  materials_url: p.materials_url || "", topics: p.topics || [],
  site_ids: p.site_ids || [],
});

function ProgramModal({ mode, id, initial, sites = [], onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [topicInput, setTopicInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSite = (sid) => {
    setForm((f) => ({
      ...f,
      site_ids: f.site_ids.includes(sid)
        ? f.site_ids.filter((x) => x !== sid)
        : [...f.site_ids, sid],
    }));
  };
  const scopeMode = form.site_ids.length === 0 ? "corporate" : "sites";
  const setScopeMode = (mode) => {
    if (mode === "corporate") set("site_ids", []);
  };

  const addTopic = () => {
    const v = topicInput.trim();
    if (!v) return;
    set("topics", [...form.topics, v]); setTopicInput("");
  };
  const removeTopic = (i) => set("topics", form.topics.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form };
      if (!payload.materials_url) payload.materials_url = "";
      if (mode === "add") await api.post("/training/programs", payload);
      else await api.put(`/training/programs/${id}`, payload);
      onSaved();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose} data-testid="program-modal-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }} data-testid="program-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="vb-heading text-xl m-0">{mode === "add" ? "New program" : "Edit program"}</h2>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="program-modal-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="program-form">
          <div>
            <label className="text-xs opacity-70">Title *</label>
            <input className="vb-input" required maxLength={200} value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. ISO 22000:2018 Awareness, HACCP Refresher"
              data-testid="program-title" />
          </div>
          <div>
            <label className="text-xs opacity-70">Description</label>
            <textarea className="vb-input" rows={2} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="program-description" />
          </div>
          <div>
            <label className="text-xs opacity-70">Applies to</label>
            <div className="flex gap-3 mt-1 flex-wrap" data-testid="program-scope-toggle">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="program-scope" checked={scopeMode === "corporate"}
                  onChange={() => setScopeMode("corporate")}
                  data-testid="program-scope-corporate" />
                <Globe size={12} /> Corporate-wide (all sites)
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="program-scope" checked={scopeMode === "sites"}
                  onChange={() => set("site_ids", form.site_ids.length ? form.site_ids : (sites[0] ? [sites[0].id] : []))}
                  data-testid="program-scope-sites" />
                <Building2 size={12} /> Specific sites only
              </label>
            </div>
            {scopeMode === "sites" && (
              <div className="vb-card p-2 mt-2 border max-h-36 overflow-y-auto" data-testid="program-sites-picker">
                {sites.length === 0 && <div className="text-xs opacity-60 p-1">No sites configured yet.</div>}
                {sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 py-0.5 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.site_ids.includes(s.id)}
                      onChange={() => toggleSite(s.id)}
                      data-testid={`program-site-${s.id}`} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs opacity-70">Category</label>
              <select className="vb-input" value={form.category}
                onChange={(e) => set("category", e.target.value)}
                data-testid="program-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs opacity-70">Duration (min)</label>
              <input className="vb-input" type="number" min={5} max={600} value={form.default_duration_minutes}
                onChange={(e) => set("default_duration_minutes", Number(e.target.value))}
                data-testid="program-duration" />
            </div>
            <div>
              <label className="text-xs opacity-70">Target competency</label>
              <select className="vb-input" value={form.target_competency_level}
                onChange={(e) => set("target_competency_level", Number(e.target.value))}
                data-testid="program-competency">
                {[1, 2, 3].map((lvl) => <option key={lvl} value={lvl}>{lvl} — {COMPETENCY[lvl].code} ({COMPETENCY[lvl].label})</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs opacity-70">Retrain every (months)</label>
              <input className="vb-input" type="number" min={0} max={120} value={form.default_retrain_months ?? ""}
                onChange={(e) => set("default_retrain_months", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="Leave blank for no retrain cadence"
                data-testid="program-retrain-months" />
            </div>
            <div>
              <label className="text-xs opacity-70">Materials URL</label>
              <input className="vb-input" value={form.materials_url}
                onChange={(e) => set("materials_url", e.target.value)}
                placeholder="https://..." data-testid="program-materials" />
            </div>
          </div>
          <div>
            <label className="text-xs opacity-70">Topics / checklist (in order)</label>
            <div className="flex gap-2 mt-1">
              <input className="vb-input flex-1" value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
                placeholder="Add topic and press Enter" data-testid="program-topic-input" />
              <button type="button" className="vb-btn" onClick={addTopic} data-testid="program-topic-add">Add</button>
            </div>
            {form.topics.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="program-topics-list">
                {form.topics.map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="opacity-60 text-xs w-5">{i + 1}.</span>
                    <span className="flex-1">{t}</span>
                    <button type="button" className="vb-btn ghost" onClick={() => removeTopic(i)} title="Remove">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="program-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="program-submit">
              {busy ? "Saving…" : mode === "add" ? "Create program" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}


// ============================================================================
//  SESSIONS — F01 hosted training events
// ============================================================================
const STATUS_PILL = {
  planned:     { label: "Planned",      cls: "vb-pill" },
  in_progress: { label: "In progress",  cls: "vb-pill warn" },
  completed:   { label: "Completed",    cls: "vb-pill ok" },
  cancelled:   { label: "Cancelled",    cls: "vb-pill" },
};

function SessionsTab() {
  const { user } = useAuth();
  const { activeSiteId, isAllSites, sites } = useSite();
  const { confirm } = useDialog();
  const canWrite = ["owner", "manager", "supervisor"].includes(user?.role);
  const canDelete = ["owner", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeSiteId && activeSiteId !== "all") params.site_id = activeSiteId;
      const { data } = await api.get("/training/sessions", { params });
      setItems(data.sessions || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load sessions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId]);

  const filtered = useMemo(() => {
    if (!filter) return items;
    return items.filter((s) => s.status === filter);
  }, [items, filter]);

  const del = async (s) => {
    if (!await confirm({
      title: `Delete session "${s.title}"?`,
      message: s.status === "completed"
        ? "This will ALSO delete every training record this session created on attendees' profiles. This cannot be undone."
        : "This session will be removed.",
      danger: true, confirmLabel: "Delete session",
    })) return;
    try { await api.delete(`/training/sessions/${s.id}`); load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  if (loading) return <div className="vb-card p-6 opacity-70" data-testid="sessions-loading">Loading sessions…</div>;
  if (openId) {
    return <SessionDetail
      id={openId} onBack={() => { setOpenId(null); load(); }}
      canWrite={canWrite} canDelete={canDelete} />;
  }

  return (
    <div data-testid="sessions-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs opacity-70">Filter:</span>
          <select className="vb-input text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sessions-filter">
            <option value="">All ({items.length})</option>
            {Object.keys(STATUS_PILL).map((s) => <option key={s} value={s}>{STATUS_PILL[s].label}</option>)}
          </select>
        </div>
        {canWrite && (
          <button className="vb-btn text-sm" onClick={() => setModal({ mode: "add" })} data-testid="add-session-btn">
            <Plus size={14} /> Host a session
          </button>
        )}
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="sessions-error">{err}</div>}

      {filtered.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="sessions-empty">
          <Users2 size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm m-0">No sessions yet.</p>
          {canWrite && <p className="text-xs mt-1">Host a training session, pick attendees, evaluate them — completion auto-creates records on each profile.</p>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="sessions-list">
          {filtered.map((s) => (
            <SessionRow key={s.id} session={s}
              canDelete={canDelete}
              onOpen={() => setOpenId(s.id)} onDelete={() => del(s)} />
          ))}
        </div>
      )}

      {modal && (
        <SessionModal
          defaultSiteId={isAllSites ? null : activeSiteId}
          sites={sites}
          onClose={() => setModal(null)}
          onSaved={(id) => { setModal(null); load(); if (id) setOpenId(id); }}
        />
      )}
    </div>
  );
}

function SessionRow({ session: s, canDelete, onOpen, onDelete }) {
  const st = STATUS_PILL[s.status] || STATUS_PILL.planned;
  const passed = (s.attendees || []).filter((a) => a.evaluation_result === "pass").length;
  const failed = (s.attendees || []).filter((a) => a.evaluation_result === "fail").length;
  return (
    <div className="vb-card p-3 border flex items-start justify-between gap-3 flex-wrap"
      data-testid={`session-row-${s.id}`}>
      <button className="flex-1 min-w-[260px] text-left" onClick={onOpen} data-testid={`open-session-${s.id}`}>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <strong className="text-sm">{s.title}</strong>
          <span className={`${st.cls} text-[10px]`}>{st.label}</span>
          {s.program_id && <span className="vb-pill text-[10px]" style={{ background: "rgba(123, 74, 45, 0.12)" }}>via program</span>}
          {s.site_id
            ? <span className="vb-pill text-[10px]" title="Hosted at a specific site"><Building2 size={10} className="inline -mt-0.5 mr-0.5" /> Site</span>
            : <span className="vb-pill ok text-[10px]" title="Corporate-wide session"><Globe size={10} className="inline -mt-0.5 mr-0.5" /> Corporate</span>}
        </div>
        <div className="text-xs opacity-70 flex items-center gap-3 flex-wrap">
          <span><Calendar size={11} className="inline -mt-0.5 mr-1" />{fmtDate(s.session_date)}</span>
          {s.trainer_name && <span>Trainer: <strong className="opacity-90">{s.trainer_name}</strong></span>}
          {s.location && <span><MapPin size={11} className="inline -mt-0.5 mr-1" />{s.location}</span>}
          <span>Attendees: {(s.attendees || []).length}</span>
          {s.status === "completed" && (
            <span>· {passed} pass{passed === 1 ? "" : "es"}{failed ? ` · ${failed} failed` : ""}</span>
          )}
        </div>
      </button>
      <div className="flex items-center gap-1">
        <button className="vb-btn ghost text-sm" onClick={onOpen} data-testid={`session-open-${s.id}`}>
          Open <ChevronRight size={13} />
        </button>
        {canDelete && (
          <button className="vb-btn ghost" onClick={onDelete} title="Delete" data-testid={`session-delete-${s.id}`}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function SessionModal({ onClose, onSaved, defaultSiteId = null, sites = [] }) {
  const [form, setForm] = useState({
    title: "", description: "", program_id: "",
    trainer_name: "", session_date: new Date().toISOString().slice(0, 10),
    duration_minutes: 60, location: "",
    attendees: [], site_id: defaultSiteId || "",
  });
  const [programs, setPrograms] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get("/training/programs"),
          api.get("/tenant/directory"),
        ]);
        setPrograms(p.data.programs || []);
        setMembers((m.data.users || []).filter((u) => u.role !== "super_admin" && !u.is_archived));
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const applyProgram = (pid) => {
    set("program_id", pid);
    const p = programs.find((x) => x.id === pid);
    if (p) {
      setForm((f) => ({
        ...f,
        title: f.title || p.title,
        description: f.description || p.description || "",
        duration_minutes: p.default_duration_minutes || f.duration_minutes,
      }));
    }
  };

  const toggleAttendee = (m) => {
    const exists = form.attendees.find((a) => a.user_id === m.id);
    if (exists) set("attendees", form.attendees.filter((a) => a.user_id !== m.id));
    else set("attendees", [...form.attendees, { user_id: m.id, department: m.role || "" }]);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    if (form.attendees.length === 0) {
      setErr("Pick at least one attendee before creating a session.");
      setBusy(false);
      return;
    }
    try {
      const payload = {
        ...form,
        program_id: form.program_id || null,
        site_id: form.site_id || null,
      };
      const { data } = await api.post("/training/sessions", payload);
      onSaved(data.session?.id);
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose} data-testid="session-modal-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="session-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="vb-heading text-xl m-0">Host a training session</h2>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="session-modal-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="session-form">
          <div>
            <label className="text-xs opacity-70">Use a program template</label>
            <select className="vb-input" value={form.program_id} onChange={(e) => applyProgram(e.target.value)} data-testid="session-program">
              <option value="">— None (free-form session) —</option>
              {programs.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.category}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs opacity-70">Title *</label>
            <input className="vb-input" required maxLength={200} value={form.title}
              onChange={(e) => set("title", e.target.value)} data-testid="session-title" />
          </div>
          <div>
            <label className="text-xs opacity-70">Description</label>
            <textarea className="vb-input" rows={2} value={form.description}
              onChange={(e) => set("description", e.target.value)} data-testid="session-description" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs opacity-70">Trainer</label>
              <input className="vb-input" value={form.trainer_name}
                onChange={(e) => set("trainer_name", e.target.value)} data-testid="session-trainer" />
            </div>
            <div>
              <label className="text-xs opacity-70">Date *</label>
              <input className="vb-input" type="date" required value={form.session_date}
                onChange={(e) => set("session_date", e.target.value)} data-testid="session-date" />
            </div>
            <div>
              <label className="text-xs opacity-70">Duration (min)</label>
              <input className="vb-input" type="number" min={5} max={600} value={form.duration_minutes}
                onChange={(e) => set("duration_minutes", Number(e.target.value))} data-testid="session-duration" />
            </div>
          </div>
          <div>
            <label className="text-xs opacity-70">Hosted at</label>
            <select className="vb-input" value={form.site_id} onChange={(e) => set("site_id", e.target.value)}
              data-testid="session-site">
              <option value="">Corporate-wide (all sites)</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <p className="text-xs opacity-60 mt-1">
              {form.site_id ? "This session will be filtered to the selected site." : "This session will be visible from every site."}
            </p>
          </div>
          <div>
            <label className="text-xs opacity-70">Location (room / venue)</label>
            <input className="vb-input" value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="e.g. Main kitchen briefing room" data-testid="session-location" />
          </div>
          <div>
            <label className="text-xs opacity-70">Attendees ({form.attendees.length} selected)</label>
            <div className="vb-card p-2 max-h-44 overflow-y-auto border" data-testid="session-attendees-picker">
              {members.length === 0 && <div className="text-xs opacity-60 p-2">No team members yet.</div>}
              {members.map((m) => {
                const selected = !!form.attendees.find((a) => a.user_id === m.id);
                return (
                  <label key={m.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm hover:opacity-80" data-testid={`session-attendee-${m.id}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleAttendee(m)} />
                    <span className="flex-1">{m.name}</span>
                    <span className="text-xs opacity-60">{m.role}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="session-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="session-submit">
              {busy ? "Saving…" : "Create session"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function SessionDetail({ id, onBack, canWrite, canDelete }) {
  const { confirm } = useDialog();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/training/sessions/${id}`);
      setS(data.session);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); }, [id]);

  if (!s) return <div className="vb-card p-6 opacity-70" data-testid="session-detail-loading">Loading session…</div>;
  const st = STATUS_PILL[s.status] || STATUS_PILL.planned;
  const locked = s.status === "completed";

  const start = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/training/sessions/${id}/start`); await load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const complete = async () => {
    // Block completion if any "attended" attendee is missing grading or
    // retrain date. Absentees (attended=false) and pending evals are excluded.
    const attended = (s.attendees || []).filter((a) => a.attended);
    const missing = attended.filter(
      (a) => !["pass", "fail"].includes(a.evaluation_result) || !a.retrain_date,
    );
    if (missing.length > 0) {
      setErr(
        `${missing.length} attendee${missing.length === 1 ? "" : "s"} still need a grading (Pass / Failed) and a retrain due date before this session can be completed. Remarks are optional.`,
      );
      return;
    }
    if (attended.length === 0) {
      if (!await confirm({
        title: "Complete an empty session?",
        message: "No attendee is marked as attended — no training records will be created on member profiles. Are you sure you want to close this session?",
        confirmLabel: "Complete anyway",
      })) return;
    } else if (!await confirm({
      title: "Complete this session?",
      message: `Each of the ${attended.length} attended attendee${attended.length === 1 ? " will receive a" : "s will receive"} training record${attended.length === 1 ? "" : "s"} on their profile. Completed sessions are locked from supervisor edits.`,
      confirmLabel: "Complete session",
    })) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/training/sessions/${id}/complete`);
      setS(data.session);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const updateAttendee = async (a, patch) => {
    setBusy(true); setErr("");
    try {
      const body = {
        user_id: a.user_id,
        attended: patch.attended ?? a.attended ?? true,
        evaluation_result: patch.evaluation_result ?? a.evaluation_result ?? "pending",
        retrain_date: patch.retrain_date ?? a.retrain_date ?? null,
        remarks: patch.remarks ?? a.remarks ?? "",
      };
      const { data } = await api.put(`/training/sessions/${id}/attendees/${a.user_id}`, body);
      setS(data.session);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="session-detail">
      <button className="vb-btn ghost text-sm mb-3" onClick={onBack} data-testid="session-detail-back">← Back to sessions</button>
      <div className="vb-card p-4 mb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="vb-heading text-lg m-0">{s.title}</h2>
              <span className={`${st.cls} text-[10px]`}>{st.label}</span>
            </div>
            <p className="text-xs opacity-70 mt-1 mb-0">{s.description}</p>
          </div>
          {canWrite && !locked && (
            <div className="flex gap-2">
              {s.status === "planned" && (
                <button className="vb-btn text-sm" onClick={start} disabled={busy} data-testid="session-start">
                  <Play size={13} /> Start
                </button>
              )}
              {(s.status === "planned" || s.status === "in_progress") && (
                <button className="vb-btn text-sm" onClick={complete} disabled={busy} data-testid="session-complete">
                  <CheckCircle2 size={13} /> Complete &amp; fan-out
                </button>
              )}
            </div>
          )}
          {locked && (
            <span className="text-xs opacity-70 inline-flex items-center gap-1">
              <Lock size={11} /> Records fanned out
            </span>
          )}
        </div>
        <div className="text-xs opacity-70 flex items-center gap-3 flex-wrap mt-1">
          <span><Calendar size={11} className="inline -mt-0.5 mr-1" />{fmtDate(s.session_date)}</span>
          {s.trainer_name && <span>Trainer: <strong className="opacity-90">{s.trainer_name}</strong></span>}
          {s.location && <span><MapPin size={11} className="inline -mt-0.5 mr-1" />{s.location}</span>}
          <span><Clock size={11} className="inline -mt-0.5 mr-1" />{s.duration_minutes} min</span>
        </div>
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="session-detail-error">{err}</div>}

      <div className="vb-card p-4" data-testid="session-roster">
        <h3 className="vb-heading text-base m-0 mb-3">Roster &amp; evaluation</h3>
        {(s.attendees || []).length === 0 ? (
          <p className="text-sm opacity-60">No attendees on this session.</p>
        ) : (
          <>
            {/* Column headers (md+ only — the grid collapses to single column on mobile) */}
            <div
              className="hidden md:grid grid-cols-12 gap-2 px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider opacity-60"
              style={{ color: "var(--vb-text-2)" }}
              data-testid="attendee-headers"
            >
              <div className="col-span-3">Member</div>
              <div className="col-span-2">Attendance</div>
              <div className="col-span-2">Grading (Pass / Failed)</div>
              <div className="col-span-2">Retrain due date</div>
              <div className="col-span-3">Remarks (optional)</div>
            </div>
            <div className="space-y-2">
              {(s.attendees || []).map((a) => (
                <AttendeeRow key={a.user_id} a={a} canWrite={canWrite && !locked} onChange={(patch) => updateAttendee(a, patch)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AttendeeRow({ a, canWrite, onChange }) {
  return (
    <div className="border rounded p-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center text-sm"
      data-testid={`attendee-row-${a.user_id}`}>
      <div className="md:col-span-3">
        <strong>{a.user_name || a.user_id}</strong>
        <div className="text-xs opacity-60">{a.user_email}</div>
      </div>
      <div className="md:col-span-2">
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={!!a.attended}
            disabled={!canWrite}
            onChange={(e) => onChange({ attended: e.target.checked })}
            data-testid={`attendee-attended-${a.user_id}`} />
          Attended
        </label>
      </div>
      <div className="md:col-span-2">
        <select className="vb-input text-xs" value={a.evaluation_result || "pending"}
          disabled={!canWrite}
          onChange={(e) => onChange({ evaluation_result: e.target.value })}
          data-testid={`attendee-eval-${a.user_id}`}>
          <option value="pending">Pending</option>
          <option value="pass">Pass</option>
          <option value="fail">Failed</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <input className="vb-input text-xs" type="date" value={a.retrain_date || ""}
          disabled={!canWrite}
          onChange={(e) => onChange({ retrain_date: e.target.value || null })}
          placeholder="Retrain date"
          data-testid={`attendee-retrain-${a.user_id}`} />
      </div>
      <div className="md:col-span-3">
        <input className="vb-input text-xs" value={a.remarks || ""}
          disabled={!canWrite}
          onChange={(e) => onChange({ remarks: e.target.value })}
          onBlur={(e) => onChange({ remarks: e.target.value })}
          placeholder="Remarks"
          data-testid={`attendee-remarks-${a.user_id}`} />
      </div>
    </div>
  );
}


// ============================================================================
//  INDUCTION — F03 per-employee onboarding checklist
// ============================================================================
function InductionTab() {
  const [params, setParams] = useSearchParams();
  const { activeSiteId, isAllSites, sites } = useSite();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(params.get("user") || null);

  // Map site_id → name so the table can render a Site column when viewing
  // "All sites". When a specific site is selected the column is hidden because
  // every row would carry the same value (redundant noise).
  const siteNameById = useMemo(() => {
    const m = {};
    for (const s of sites || []) m[s.id] = s.name;
    return m;
  }, [sites]);

  const load = async () => {
    setLoading(true);
    try {
      // SiteContext uses the string "all" as the all-sites sentinel — convert
      // to a missing param so the backend returns everyone instead of trying
      // to filter on a fake site_id.
      const qp = {};
      if (activeSiteId && activeSiteId !== "all") qp.site_id = activeSiteId;
      const { data } = await api.get("/training/induction/overview", { params: qp });
      setRows(data.members || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load induction overview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId]);

  const setOpen = (id) => {
    setOpenId(id);
    const next = new URLSearchParams(params);
    if (id) next.set("user", id); else next.delete("user");
    setParams(next, { replace: true });
  };

  if (loading) return <div className="vb-card p-6 opacity-70" data-testid="induction-loading">Loading induction roster…</div>;
  if (openId) return <InductionDetail userId={openId} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div data-testid="induction-tab">
      {err && <div className="vb-pill err mb-3" data-testid="induction-error">{err}</div>}
      {rows.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="induction-empty">
          <ClipboardCheck size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm m-0">No team members yet.</p>
        </div>
      ) : (
        <div className="vb-card p-0 overflow-x-auto" data-testid="induction-list">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.04)" }}>
                <th className="text-left p-2">Member</th>
                <th className="text-left p-2">Role</th>
                {isAllSites && <th className="text-left p-2" data-testid="induction-site-header">Site location</th>}
                <th className="text-left p-2">Hire date</th>
                <th className="text-left p-2">Progress</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <InductionRow key={m.id} row={m} showSite={isAllSites} siteNameById={siteNameById}
                  onOpen={() => setOpen(m.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InductionRow({ row: m, onOpen, showSite, siteNameById }) {
  const pct = m.progress.total > 0 ? Math.round((m.progress.done / m.progress.total) * 100) : 0;
  const status = m.progress.status;
  const statusPill = status === "signed_off"
    ? { cls: "vb-pill ok", label: "Signed off" }
    : status === "complete_awaiting_signoff"
    ? { cls: "vb-pill warn", label: "Awaiting sign-off" }
    : status === "in_progress"
    ? { cls: "vb-pill", label: "In progress" }
    : { cls: "vb-pill", label: "Not started" };
  // When viewing "All sites" we surface the user's assigned site(s) so admins
  // can keep context. Users mapped to multiple sites get a "+N" suffix.
  let siteCell = null;
  if (showSite) {
    const ids = m.site_ids || [];
    if (ids.length === 0) {
      siteCell = <span className="opacity-50">— Unassigned</span>;
    } else {
      const first = siteNameById?.[ids[0]] || "Site";
      siteCell = ids.length === 1
        ? <span>{first}</span>
        : <span>{first} <span className="opacity-60">+{ids.length - 1}</span></span>;
    }
  }
  return (
    <tr className="border-t" data-testid={`induction-row-${m.id}`}>
      <td className="p-2"><strong>{m.name}</strong><div className="text-xs opacity-60">{m.email}</div></td>
      <td className="p-2 text-xs opacity-80">{m.role}</td>
      {showSite && (
        <td className="p-2 text-xs opacity-80" data-testid={`induction-site-${m.id}`}>{siteCell}</td>
      )}
      <td className="p-2 text-xs opacity-80">{fmtDate(m.hire_date)}</td>
      <td className="p-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded" style={{ background: "rgba(0,0,0,0.08)" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "var(--vb-text-2)", borderRadius: 8 }} />
          </div>
          <span className="opacity-60">{m.progress.done}/{m.progress.total}</span>
        </div>
      </td>
      <td className="p-2"><span className={`${statusPill.cls} text-[10px]`}>{statusPill.label}</span></td>
      <td className="p-2 text-right">
        <button className="vb-btn ghost text-sm" onClick={onOpen} data-testid={`open-induction-${m.id}`}>
          Open <ChevronRight size={13} />
        </button>
      </td>
    </tr>
  );
}

function InductionDetail({ userId, onBack }) {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const canWrite = ["owner", "manager", "supervisor"].includes(user?.role);
  const canReopen = ["owner", "manager"].includes(user?.role);
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [signoffRemarks, setSignoffRemarks] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/training/induction/users/${userId}`);
      setRec(data.record);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); }, [userId]);

  if (!rec) return <div className="vb-card p-6 opacity-70" data-testid="induction-detail-loading">Loading induction…</div>;
  const locked = !!rec.supervisor_signed_at;

  const save = async (nextTopics, nextRemarks) => {
    if (locked) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.put(`/training/induction/users/${userId}`, {
        topics: nextTopics ?? rec.topics,
        overall_remarks: nextRemarks ?? rec.overall_remarks ?? "",
      });
      setRec(data.record);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleTopic = (idx) => {
    const next = rec.topics.map((t, i) => i === idx
      ? { ...t, completed: !t.completed, completed_at: !t.completed ? new Date().toISOString() : null }
      : t);
    save(next);
  };
  const updateTopicField = (idx, field, value) => {
    const next = rec.topics.map((t, i) => i === idx ? { ...t, [field]: value } : t);
    save(next);
  };

  const signOff = async () => {
    if (!await confirm({
      title: "Sign off induction?",
      message: "This locks the record for compliance. Owner/manager can reopen later.",
      confirmLabel: "Sign off",
    })) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/training/induction/users/${userId}/signoff`, { remarks: signoffRemarks });
      setRec(data.record); setSignoffRemarks("");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Sign-off failed"); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    if (!await confirm({ title: "Reopen induction record?", message: "Sign-off will be cleared.", confirmLabel: "Reopen" })) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/training/induction/users/${userId}/reopen`);
      setRec(data.record);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Reopen failed"); }
    finally { setBusy(false); }
  };

  const allDone = rec.topics.length > 0 && rec.topics.every((t) => t.completed);
  const allTrainersFilled = rec.topics.length > 0 && rec.topics.every((t) => (t.trainer_name || "").trim().length > 0);
  const readyToSignOff = allDone && allTrainersFilled;
  const missingTrainerCount = rec.topics.filter((t) => !(t.trainer_name || "").trim()).length;

  return (
    <div data-testid="induction-detail">
      <button className="vb-btn ghost text-sm mb-3" onClick={onBack} data-testid="induction-detail-back">← Back to roster</button>
      <div className="vb-card p-4 mb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h2 className="vb-heading text-lg m-0">Induction checklist (VB-P-072 F03)</h2>
            <p className="text-xs opacity-70 m-0">Hire date: {fmtDate(rec.hire_date)} · {rec.progress.done}/{rec.progress.total} topics complete</p>
          </div>
          {locked ? (
            <div className="text-right">
              <span className="vb-pill ok text-[10px]"><CheckCircle2 size={10} /> Signed off</span>
              <div className="text-xs opacity-70 mt-1">{fmtDate(rec.supervisor_signed_at)}</div>
            </div>
          ) : (
            allDone && canWrite && (
              <span className="vb-pill warn text-[10px]"><AlertTriangle size={10} /> Ready to sign off</span>
            )
          )}
        </div>
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="induction-detail-error">{err}</div>}

      <div className="vb-card p-0 overflow-x-auto mb-3">
        <table className="w-full text-sm" data-testid="induction-topics">
          <thead>
            <tr style={{ background: "rgba(0,0,0,0.04)" }}>
              <th className="text-left p-2 w-10">#</th>
              <th className="text-left p-2 w-10"></th>
              <th className="text-left p-2">Program</th>
              <th className="text-left p-2">Trainer</th>
              <th className="text-left p-2">Completed</th>
              <th className="text-left p-2">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rec.topics.map((t, i) => (
              <tr key={t.key} className="border-t" data-testid={`induction-topic-${t.key}`}>
                <td className="p-2 opacity-60">{i + 1}</td>
                <td className="p-2">
                  <input type="checkbox" checked={!!t.completed}
                    disabled={!canWrite || locked}
                    onChange={() => toggleTopic(i)}
                    data-testid={`induction-check-${t.key}`} />
                </td>
                <td className="p-2">{t.label}</td>
                <td className="p-2">
                  <input className="vb-input text-xs"
                    disabled={!canWrite || locked}
                    onBlur={(e) => updateTopicField(i, "trainer_name", e.target.value)}
                    defaultValue={t.trainer_name || ""}
                    key={`trainer-${t.key}-${t.trainer_name || ""}`}
                    placeholder="Trainer name"
                    data-testid={`induction-trainer-${t.key}`} />
                </td>
                <td className="p-2">
                  {(() => {
                    // Date is editable when the topic is ticked. Defaults
                    // to today (set by toggleTopic when first ticked) but
                    // owners/managers can back-date if the actual
                    // training happened earlier. Future dates blocked.
                    const today = new Date().toISOString().slice(0, 10);
                    const cur = t.completed_at ? t.completed_at.slice(0, 10) : "";
                    return (
                      <input type="date"
                        className="vb-input text-xs"
                        style={{ maxWidth: 140 }}
                        disabled={!canWrite || locked || !t.completed}
                        max={today}
                        value={cur}
                        onChange={(e) => {
                          // Convert YYYY-MM-DD → midday UTC ISO. Midday avoids
                          // timezone day-shift on display in `fmtDate`.
                          const v = e.target.value;
                          if (!v) return;
                          const iso = new Date(`${v}T12:00:00Z`).toISOString();
                          updateTopicField(i, "completed_at", iso);
                        }}
                        data-testid={`induction-date-${t.key}`} />
                    );
                  })()}
                </td>
                <td className="p-2">
                  <input className="vb-input text-xs"
                    disabled={!canWrite || locked}
                    onBlur={(e) => updateTopicField(i, "remarks", e.target.value)}
                    defaultValue={t.remarks || ""}
                    key={`remarks-${t.key}-${t.remarks || ""}`}
                    placeholder="Remarks"
                    data-testid={`induction-remarks-${t.key}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!locked && canWrite && (
        <div className="vb-card p-4" data-testid="induction-signoff-block">
          <h3 className="vb-heading text-base m-0 mb-2">
            <FileSignature size={14} className="inline -mt-0.5 mr-1" /> Supervisor sign-off
          </h3>
          <textarea className="vb-input" rows={2} value={signoffRemarks}
            onChange={(e) => setSignoffRemarks(e.target.value)}
            placeholder="Remarks (optional)"
            data-testid="induction-signoff-remarks" />
          <div className="flex justify-end mt-2">
            <button className="vb-btn text-sm" disabled={busy || !readyToSignOff} onClick={signOff} data-testid="induction-signoff-btn">
              <FileSignature size={13} /> Sign off
            </button>
          </div>
          {!allDone && (
            <p className="text-xs opacity-60 mt-2 m-0">Complete every topic before sign-off.</p>
          )}
          {allDone && !allTrainersFilled && (
            <p className="text-xs mt-2 m-0" style={{ color: "var(--vb-error)" }} data-testid="induction-trainer-missing">
              Trainer name is required on every topic before sign-off — {missingTrainerCount} topic{missingTrainerCount === 1 ? "" : "s"} still missing a trainer. Remarks remain optional.
            </p>
          )}
        </div>
      )}

      {locked && canReopen && (
        <div className="flex justify-end">
          <button className="vb-btn ghost text-sm" disabled={busy} onClick={reopen} data-testid="induction-reopen">
            Reopen record
          </button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};
